import React from 'react';

import { Body3, Body4, Caption2, T3, Wrap } from 'components/Common';
import Spacing from 'components/Common/Spacing';
import useIsMobile from 'hooks/useIsMobile';
import { Seo } from 'shared/seo';

import { ContractManagementBlock, GuideBackgroundBlock, LinkButton } from './styles';

// 요금 FAQ 답변 — 모바일/데스크톱 분기 양쪽에서 공유(공개 페이지: 요율 비공개 원칙에 따라 금액 없음)
const SERVICE_FEE_FAQ_ANSWER = (
  <>
    매물 등록과 관리는 무료로 제공됩니다. 계약이 성사된 경우에만 서비스 이용료가 부과되며,
    <br />
    구체적인 금액과 정산 방식은 입점 후 로그인 시 확인하실 수 있습니다.
  </>
);

function GuidePage() {
  const isMobile = useIsMobile();
  const sectionImg = isMobile ? { maxWidth: '60%', height: 'auto' as const } : undefined;

  const seo = (
    <Seo
      title="이용 가이드 | 체크메이트코리아"
      description="체크메이트코리아 이용 가이드입니다. 부동산·숙박업소 회원이 매물을 등록·홍보하고 외국인 고객과 비대면 가계약을 체결하는 방법을 안내합니다."
      path="/guide"
    />
  );

  if (isMobile) {
    const center = { textAlign: 'center' as const, wordBreak: 'keep-all' as const };
    const left = { textAlign: 'left' as const, wordBreak: 'keep-all' as const };
    const grayBox = { ...left, borderRadius: '12px' };
    const pinkBox = { ...left, borderRadius: '12px' };
    const head = { fontWeight: 600 as const }; // 헤딩 700 → 600
    const body14 = { fontSize: '14px', fontWeight: 400 as const }; // 본문 → 14px/400
    const cap = { fontWeight: 400 as const }; // 박스/유의사항 본문 600 → 400
    const label = { fontWeight: 600 as const }; // 박스 라벨·유의사항 라벨
    // 구두점(・, 1.) 행잉 인덴트: 줄바꿈 시 마커 뒤 텍스트와 같은 x에 정렬
    const hang = { paddingLeft: '1.2em', textIndent: '-1.2em' } as const;
    // 번호 목록: 숫자 고정폭(tabular)으로 1./2./3. 텍스트 시작 위치 일치
    const num = { ...cap, ...hang, fontVariantNumeric: 'tabular-nums' as const };
    return (
      <Wrap direction="column" width="100%" className="guide-mobile">
        {seo}
        {/* 히어로 */}
        <Wrap
          direction="column"
          align="center"
          width="100%"
          backgroundColor="Gray0"
          padding="32px 20px 40px"
          style={center}
        >
          <img src="GuideImage.png" alt="안내" style={{ width: '100%', maxWidth: '320px', height: 'auto' }} />
          <Spacing height={24} />
          <T3 style={head}>체크메이트코리아는</T3>
          <T3 style={head}>한국에 장기 거주하는 외국인을 위한</T3>
          <T3 style={head}>주거 계약 지원 서비스입니다.</T3>
          <Spacing height={18} />
          <Body4 color="Gray40" style={body14}>
            외국인 고객은 서비스 페이지에서 부동산/숙박업소 매물을 조회하고, 계약을 체결할 수 있습니다.
          </Body4>
          <Spacing height={10} />
          <Body4 color="Gray40" style={body14}>
            부동산/숙박업소 회원은 체크메이트코리아를 통해 매물을 홍보하고 계약을 체결할 수 있습니다. 하단 버튼을 통해
            외국인 고객의 서비스 페이지를 확인해보세요.
          </Body4>
          <Spacing height={24} />
          <LinkButton
            href="https://checkmatekorea.com/"
            target="_blank"
            rel="noreferrer"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center',
            }}
          >
            <Caption2 color="White">체크메이트 서비스 바로가기</Caption2>
          </LinkButton>
        </Wrap>

        {/* 매물 관리 */}
        <Wrap direction="column" align="center" width="100%" backgroundColor="White" padding="48px 20px" style={center}>
          <T3 style={head}>매물 관리</T3>
          <Spacing height={20} />
          <img src="/House.svg" alt="매물 관리" style={{ width: '150px', height: 'auto' }} />
          <Spacing height={20} />
          <Body4 color="Gray50" style={body14}>
            ‘매물 관리’ 메뉴에서 매물을 등록하고 관리할 수 있습니다.
          </Body4>
          <Spacing height={24} />
          <Wrap
            direction="column"
            align="start"
            width="100%"
            gap="8px"
            backgroundColor="Gray0"
            padding="20px"
            style={grayBox}
          >
            <Body4 color="Gray50" style={{ ...body14, ...hang }}>
              ・ ‘새 매물 등록’을 눌러 매물을 추가하세요.
            </Body4>
            <Body4 color="Gray50" style={{ ...body14, ...hang }}>
              ・ 매물 수정 및 삭제가 가능합니다.
            </Body4>
            <Body4 color="Gray50" style={{ ...body14, ...hang }}>
              ・ 고객에게 노출할지 여부를 설정할 수 있습니다.
            </Body4>
          </Wrap>
          <Spacing height={20} />
          <Wrap
            direction="column"
            align="start"
            width="100%"
            gap="8px"
            backgroundColor="PastelRed"
            padding="20px"
            style={pinkBox}
          >
            <Wrap align="center" gap="4px">
              <img src="Warnning_Icon.svg" width={15} height={15} alt="유의" />
              <Caption2 color="Red" style={label}>
                유의사항
              </Caption2>
            </Wrap>
            <Caption2 color="Red" style={{ ...cap, ...hang }}>
              ・ 체크메이트코리아를 통해 계약이 완료된 매물이나 외부에서 다른 임차인과 계약이 완료된 매물은 반드시
              ‘노출off’로 변경하거나 ‘입주가능일’을 변경해주세요.
            </Caption2>
            <Caption2 color="Red" style={{ ...cap, ...hang }}>
              ・ 매물 정보는 실제 정보와 일치하는지 반드시 확인해주세요.
            </Caption2>
            <Caption2 color="Red" style={{ ...cap, ...hang }}>
              ・ 위 사항들을 위반할 경우 서비스 이용에 제한이 있을 수 있습니다.
            </Caption2>
          </Wrap>
        </Wrap>

        {/* 계약 관리 */}
        <Wrap direction="column" align="center" width="100%" backgroundColor="White" padding="48px 20px" style={center}>
          <T3 style={head}>계약 관리</T3>
          <Spacing height={20} />
          <img src="/File.svg" alt="계약 관리" style={{ width: '150px', height: 'auto' }} />
          <Spacing height={20} />
          <Body4 color="Gray50" style={body14}>
            ‘계약 관리’ 메뉴에서 고객이 문의한 가계약 신청 내역을 확인하고 처리할 수 있습니다.
          </Body4>
          <Spacing height={24} />
          <Wrap direction="column" align="start" width="100%" backgroundColor="Gray0" padding="20px" style={grayBox}>
            <Body3 color="Gray50" style={label}>
              계약 진행 과정
            </Body3>
            <Spacing height={10} />
            <Wrap direction="column" align="start" gap="8px" width="100%">
              <Caption2 color="Gray40" style={num}>
                1. 외국인 고객이 계약 희망 매물에 대해 가계약 신청을 합니다.
              </Caption2>
              <Caption2 color="Gray40" style={num}>
                2. 부동산/숙박업소 회원은 가계약 신청접수 후 24시간 이내에 계약 관리 메뉴에서 승인 또는 반려 여부를
                처리합니다.
              </Caption2>
              <Caption2 color="Gray40" style={num}>
                3. 가계약이 승인되면, 체크메이트코리아가 집주인 계좌로 가계약금을 송금합니다.
              </Caption2>
              <Caption2 color="Gray40" style={num}>
                4. 가계약금 입금이 확인되면 계약은 확정되며, 고객은 약속된 날짜에 부동산/숙박업소를 방문하여 계약을
                진행합니다.
              </Caption2>
            </Wrap>
          </Wrap>
          <Spacing height={20} />
          <Wrap
            direction="column"
            align="start"
            width="100%"
            gap="8px"
            backgroundColor="PastelRed"
            padding="20px"
            style={pinkBox}
          >
            <Wrap align="center" gap="4px">
              <img src="Warnning_Icon.svg" width={15} height={15} alt="유의" />
              <Caption2 color="Red" style={label}>
                유의사항
              </Caption2>
            </Wrap>
            <Caption2 color="Red" style={{ ...cap, ...hang }}>
              ・ 가계약 신청이 접수되면 24시간 이내에 승인 또는 반려 처리를 완료해주세요. (신청 유형에 따라 정확한 처리
              기한은 알림톡으로 별도 안내됩니다) 해당 시간 내에 처리되지 않을 경우 신청은 자동 반려됩니다. 반복적인
              미처리시 서비스 이용에 제한이 있을 수 있습니다.
            </Caption2>
            <Caption2 color="Red" style={{ ...cap, ...hang }}>
              ・ 가계약금을 제외한 보증금, 월세, 공인중개사 중개보수 등의 송금업무는 체크메이트코리아에서 담당하지
              않으므로 고객에게 별도로 송금정보를 안내해야 합니다. 송금 가이드라인이 있으시다면 체크메이트코리아
              고객센터를 통해 알려주세요.
            </Caption2>
          </Wrap>
        </Wrap>

        {/* FAQ */}
        <Wrap
          direction="column"
          align="center"
          width="100%"
          backgroundColor="White"
          padding="48px 20px 56px"
          style={center}
        >
          <T3 style={head}>FAQ</T3>
          <Spacing height={20} />
          <img src="/Check2.svg" alt="FAQ" style={{ width: '150px', height: 'auto' }} />
          <Spacing height={24} />
          <Wrap direction="column" align="start" width="100%" backgroundColor="Gray0" padding="20px" style={grayBox}>
            <Body3 color="Gray50" style={label}>
              Q. 서비스 이용료는 얼마인가요?
            </Body3>
            <Spacing height={8} />
            <Caption2 color="Gray40" style={cap}>
              {SERVICE_FEE_FAQ_ANSWER}
            </Caption2>
            <Spacing height={20} />
            <Body3 color="Gray50" style={label}>
              Q. 고객 문의는 어떻게 처리하나요?
            </Body3>
            <Spacing height={8} />
            <Caption2 color="Gray40" style={cap}>
              계약 확정 후 부동산 주소, 계약 금액 등 계약에 필요한 정보는 체크메이트코리아에서 고객에게 상세히
              안내합니다. 추가 고객 문의가 있을 경우 체크메이트코리아에서 직접 연락드릴 예정입니다.
            </Caption2>
          </Wrap>
        </Wrap>
      </Wrap>
    );
  }

  return (
    <Wrap direction="column" jutify="center" width="100%">
      {seo}
      <Wrap jutify="center" backgroundColor="Gray10" width="100%">
        <GuideBackgroundBlock>
          <img src="GuideImage.png" alt="GuideLogo" width="323px" style={sectionImg} />
          <Wrap direction="column" align="start">
            <T3>체크메이트코리아는</T3>
            <T3>한국에 장기 거주하는 외국인을 위한</T3>
            <T3>주거 계약 지원 서비스입니다.</T3>
            <Spacing height={23} />
            <Body4 color="Gray40">
              외국인 고객은 서비스 페이지에서 부동산/숙박업소 매물을 조회하고, 계약을 체결할 수 있습니다.
            </Body4>
            <Body4 color="Gray40">
              부동산/숙박업소 회원은 체크메이트코리아를 통해 매물을 홍보하고 계약을 체결할 수 있습니다.
            </Body4>
            <Body4 color="Gray40">하단 버튼을 통해 외국인 고객의 서비스 페이지를 확인해보세요.</Body4>
            <Spacing height={17} />
            <LinkButton href="https://checkmatekorea.com/" target="_blank" rel="noreferrer">
              <Caption2 color="White">체크메이트 서비스 바로가기</Caption2>
            </LinkButton>
          </Wrap>
        </GuideBackgroundBlock>
      </Wrap>

      <Wrap
        width="100%"
        direction="column"
        padding={isMobile ? '0px 16px' : '0px 186px 0px 198px'}
        align="center"
        backgroundColor="White"
      >
        <Spacing height={110} />
        <Wrap align="start" width="100%" maxWidth="1124px">
          <Wrap
            gap={isMobile ? '24px' : '104px'}
            jutify="center"
            direction={isMobile ? 'column' : 'row'}
            align={isMobile ? 'center' : 'center'}
          >
            <img src="/House.svg" alt="House" width="230px" height="230px" style={sectionImg} />
            <Wrap direction="column" align="start">
              <T3>매물 관리</T3>
              <Spacing height={12} />
              <Body4 color="Gray50">상단 메뉴에서 ‘매물 관리’ 탭에서 매물을 등록하고 관리할 수 있습니다.</Body4>
              <Body4 color="Gray50">페이지 우측의 매물 등록하기 버튼을 눌러 매물을 추가하세요.</Body4>
              <Body4 color="Gray50">매물 수정 및 삭제가 가능하며 고객에게 노출할지 여부도 설정할 수 있습니다.</Body4>
              <Spacing height={35} />
              <Wrap align="center">
                <span>
                  <img src="Warnning_Icon.svg" alt="Warnning_Icon" width={15} height={15} />
                </span>
                <Caption2 color="Red">유의사항</Caption2>
              </Wrap>
              <Spacing height={10} />
              <Caption2 color="Red">
                <li>체크메이트코리아를 통해 계약이 완료된 매물이나</li>
              </Caption2>
              <Caption2 color="Red">외부에서 다른 임차인과 계약이 완료된 매물은</Caption2>
              <Caption2 color="Red">반드시 ‘노출off’로 변경하거나 ‘입주가능일’을 변경해주세요.</Caption2>
              <Caption2 color="Red">
                <li>매물 정보는 실제 정보와 일치하는지 반드시 확인해주세요.</li>
              </Caption2>
              <Caption2 color="Red">
                <li>위 사항들을 위반할 경우 서비스 이용에 제한이 있을 수 있습니다.</li>
              </Caption2>
            </Wrap>
          </Wrap>
        </Wrap>
        <Spacing height={150} />
        <Wrap gap={isMobile ? '24px' : '104px'} jutify="center" direction={isMobile ? 'column' : 'row'}>
          <img src="/File.svg" alt="File" width="230px" height="230px" style={sectionImg} />
          <Wrap direction="column" align="start">
            <T3>계약 관리</T3>
            <Spacing height={12} />
            <Body4 color="Gray50">상단 메뉴 ‘계약 관리’ 탭에서 고객이 문의한</Body4>
            <Body4 color="Gray50">가계약 신청 내역을 확인하고 처리할 수 있습니다.</Body4>
            <Spacing height={25} />
            <ContractManagementBlock padding="42px 30.5px">
              <Body3 color="Gray50">계약 진행 과정</Body3>
              <Spacing height={7} />
              <Caption2 color="Gray40">1. 외국인 고객이 계약 희망 매물에 대해 가계약 신청을 합니다.</Caption2>
              <Caption2 color="Gray40">
                2. 부동산/숙박업소 회원은 가계약 신청접수 후 24시간 이내에 계약 관리 메뉴에서 승인 또는 반려 여부를
                처리합니다.
              </Caption2>
              <Caption2 color="Gray40">
                3. 가계약이 승인되면, 체크메이트코리아가 집주인 계좌로 가계약금을 송금합니다.
              </Caption2>
              <Caption2 color="Gray40">
                4. 가계약금 입금이 확인되면 계약은 확정되며, 고객은 약속된 날짜에 부동산/숙박업소를 방문하여 계약을
                진행합니다.
              </Caption2>
            </ContractManagementBlock>
            <Spacing height={25} />
            <Wrap align="center">
              <span>
                <img src="Warnning_Icon.svg" alt="Warnning_Icon" width={15} height={15} />
              </span>
              <Caption2 color="Red">유의사항</Caption2>
            </Wrap>
            <Spacing height={11} />
            <Caption2 color="Red">
              <li>가계약 신청이 접수되면 24시간 이내에 승인 또는 반려 처리를 완료해주세요.</li>
            </Caption2>
            <Caption2 color="Red">(신청 유형에 따라 정확한 처리 기한은 알림톡으로 별도 안내됩니다)</Caption2>
            <Caption2 color="Red">해당 시간 내에 처리되지 않을 경우 신청은 자동 반려됩니다.</Caption2>
            <Caption2 color="Red">반복적인 미처리시 서비스 이용에 제한이 있을 수 있습니다.</Caption2>
            <Caption2 color="Red" style={{ wordBreak: 'keep-all' }}>
              <li>
                가계약금을 제외한 보증금, 월세, 공인중개사 중개보수 등의 송금업무는 체크메이트코리아에서 담당하지
                않으므로 고객에게 별도로 송금정보를 안내해야 합니다.
                <br />
                고객에게 전달하고 싶으신 송금 가이드라인이 있으시다면 체크메이트코리아 고객센터를 통해 알려주세요. 해당
                내용을 가계약이 확정된 고객에게 전달드립니다.
              </li>
            </Caption2>
          </Wrap>
        </Wrap>
        <Spacing height={150} />
        <Wrap gap={isMobile ? '24px' : '104px'} jutify="center" direction={isMobile ? 'column' : 'row'}>
          <img src="/Check2.svg" alt="Check2" width="230px" height="230px" style={sectionImg} />
          <Wrap direction="column" align="start">
            <T3>FAQ</T3>
            <Spacing height={20} />
            <ContractManagementBlock padding="52.5px 30.5px">
              <Body3 color="Gray50">Q. 서비스 이용료는 얼마인가요?</Body3>
              <Spacing height={7} />
              <Caption2 color="Gray40">{SERVICE_FEE_FAQ_ANSWER}</Caption2>
              <Spacing height={25} />
              <Body3 color="Gray50">Q. 고객 문의는 어떻게 처리하나요?</Body3>
              <Spacing height={7} />
              <Caption2 color="Gray40">
                계약 확정 후 부동산 주소, 계약 금액 등 계약에 필요한 정보는 체크메이트코리아에서 고객에게 상세히
                안내합니다.
              </Caption2>
              <Caption2 color="Gray40">
                추가 고객 문의가 있을 경우 체크메이트코리아에서 직접 연락드릴 예정입니다.
              </Caption2>
            </ContractManagementBlock>
          </Wrap>
        </Wrap>
        <Spacing height={175} />
      </Wrap>
    </Wrap>
  );
}

export default GuidePage;
